/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, FormEvent, useCallback, useRef, useEffect } from 'react';

interface UseFormProps<T> {
    initialValues: T;
    onSubmit: (values: T) => Promise<void>;
    validate?: (values: T) => Record<string, string>;
    // New props for dirty state handling
    enableDirtyTracking?: boolean;
    onDirtyStateChange?: (isDirty: boolean) => void;
}

export const useForm = <T extends Record<string, unknown>>({
    initialValues,
    onSubmit,
    validate,
    enableDirtyTracking = true,
    onDirtyStateChange,
}: UseFormProps<T>) => {
    const [values, setValues] = useState<T>(initialValues);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);

    // Dirty state management
    const [isDirty, setIsDirty] = useState(false);
    const [initialFormData, setInitialFormData] = useState<T>(initialValues);
    const isInitializedRef = useRef(false);

    // Deep comparison function for objects
    const deepEqual = useCallback((obj1: any, obj2: any): boolean => {
        if (obj1 === obj2) return true;

        if (obj1 == null || obj2 == null) return obj1 === obj2;

        if (typeof obj1 !== typeof obj2) return false;

        if (typeof obj1 !== 'object') return obj1 === obj2;

        if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

        if (Array.isArray(obj1)) {
            if (obj1.length !== obj2.length) return false;
            return obj1.every((item, index) => deepEqual(item, obj2[index]));
        }

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) return false;

        return keys1.every(key => deepEqual(obj1[key], obj2[key]));
    }, []);

    // Check if form is dirty
    const checkFormIsDirty = useCallback(() => {
        if (!enableDirtyTracking || !isInitializedRef.current) return false;
        return !deepEqual(values, initialFormData);
    }, [values, initialFormData, deepEqual, enableDirtyTracking]);

    // Update dirty state when values change
    useEffect(() => {
        if (enableDirtyTracking && isInitializedRef.current) {
            const newIsDirty = checkFormIsDirty();
            if (newIsDirty !== isDirty) {
                setIsDirty(newIsDirty);
                onDirtyStateChange?.(newIsDirty);
            }
        }
    }, [values, checkFormIsDirty, isDirty, onDirtyStateChange, enableDirtyTracking]);

    // Initialize or update form data (for edit scenarios)
    const initializeFormData = useCallback((newInitialData: T) => {
        setValues(newInitialData);
        setInitialFormData(newInitialData);
        setIsDirty(false);
        setErrors({});
        isInitializedRef.current = true;
        onDirtyStateChange?.(false);
    }, [onDirtyStateChange]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setValues((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const clearFieldError = (fieldPath: string) => {
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[fieldPath];
            return newErrors;
        });
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        const trimmedValues = Object.fromEntries(
            Object.entries(values).map(([key, value]) => [
                key,
                typeof value === 'string' ? value.trim() : value
            ])
        ) as T;

        if (validate) {
            const validationErrors = validate(trimmedValues);
            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }
        }

        setErrors({});
        setIsLoading(true);

        try {
            await onSubmit(trimmedValues);
            // Reset dirty state after successful submission
            if (enableDirtyTracking) {
                setInitialFormData(trimmedValues);
                setIsDirty(false);
                onDirtyStateChange?.(false);
            }
        } catch (error) {
            setErrors({ submit: error instanceof Error ? error.message : 'An error occurred' });
        } finally {
            setIsLoading(false);
        }
    };

    const clearErrors = () => {
        setErrors({});
    };

    const resetForm = () => {
        setValues(initialFormData);
        setErrors({});
        setIsLoading(false);
        setIsDirty(false);
        onDirtyStateChange?.(false);
    };

    // Reset to original initial values
    const resetToInitial = () => {
        setValues(initialValues);
        setErrors({});
        setIsLoading(false);
        setIsDirty(false);
        onDirtyStateChange?.(false);
    };

    return {
        values,
        setValues,
        handleChange,
        errors,
        isLoading,
        handleSubmit,
        clearErrors,
        clearFieldError,
        setErrors,
        validate,
        resetForm,
        resetToInitial,
        // Dirty state related
        isDirty,
        initializeFormData,
        checkFormIsDirty,
    };
};
